import { CustomerResource, usePostCustomerUsersMutation } from '@/api/serverApi'
import * as v from 'valibot'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Button, InputLabel, TextBox, useErrorHandler } from '@cfs-fixings/shared'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'

const textSpec = {
  'content': 'Enter the email addresses of users you want to invite to this customer organisation. They will receive email instructions on how to complete their account setup.',
  'ownerEmailField': {
    'label': 'Owner Email',
    'placeholder': 'owner@example.com',
  },
  'emailsField': {
    'label': 'Emails',
    'placeholder': 'user1@example.com, user2@example.com...',
  },
  'cancel': 'Cancel',
  'sendInvites': 'Send Invites',
  'successToast': {
    'title': 'Invites sent successfully',
    'content': 'Invites Sent',
  },
  'errorToast': {
    'title': 'Failed to send invites',
    'content': 'An error occurred',
  },
}

type PropTypes = {
  customer: CustomerResource
  onSuccess: () => void
  onCancel: () => void
}

const inviteTeamMemberFormSchema = v.object({
  emails: v.string(),
})

type InviteTeamMemberFormInputs = v.Input<typeof inviteTeamMemberFormSchema>

const InviteTeamMemberForm = ({ customer, onSuccess, onCancel }: PropTypes) => {
  const [inviteUsers, { isLoading }] = usePostCustomerUsersMutation()

  const { handleSubmit, register, setError, formState: { errors } } = useForm<InviteTeamMemberFormInputs>({
    defaultValues: {
      emails: '',
    },
    resolver: valibotResolver(inviteTeamMemberFormSchema),
  })

  const errorHandler = useErrorHandler()

  const onSubmit: SubmitHandler<InviteTeamMemberFormInputs> = (data) => {
    inviteUsers({
      body: {
        emails: data.emails,
        customer_id: customer.id,
      },
    }).unwrap().then(() => {
      onSuccess()
      toastHelper.success(textSpec.successToast.title, textSpec.successToast.content)
    }).catch((e) => {
      errorHandler(e, setError)
      toastHelper.error(textSpec.errorToast.title, textSpec.errorToast.content)
    })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col items-start gap-10">
        <div className="text-start mt-4">
          {textSpec.content}
        </div>
        <div className="w-full">
          <InputLabel>
            {textSpec.emailsField.label}
          </InputLabel>
          <TextBox
            placeholder={textSpec.emailsField.placeholder}
            className="w-full min-h-[200px]"
            containerClassName="flex flex-col items-start gap-2"
            {...register('emails')}
            error={errors.emails?.message}
          />
        </div>
        <div className="flex gap-3 justify-end w-full">
          <Button
            variant={'outline'}
            onClick={onCancel}
          >
            {textSpec.cancel}
          </Button>
          <Button
            variant="dark"
            isLoading={isLoading}
            type="submit"
          >
            {textSpec.sendInvites}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default InviteTeamMemberForm
